import React, { useState } from 'react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { AlertTriangleIcon, SaveIcon, RefreshCwIcon } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
const SettingsPage: React.FC = () => {
  const {
    threshold,
    setThreshold
  } = useData();
  const {
    isAdmin
  } = useAuth();
  const [newThreshold, setNewThreshold] = useState(threshold);
  const [isSaving, setIsSaving] = useState(false);
  // Redirect non-admin users
  if (!isAdmin) {
    return <Navigate to="/dashboard" data-id="element-296" />;
  }
  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Update threshold
      setThreshold(newThreshold);
      localStorage.setItem('energyThreshold', newThreshold.toString());
      toast.success('Threshold updated successfully');
    } catch (error) {
      toast.error('Failed to update threshold');
    } finally {
      setIsSaving(false);
    }
  };
  return <div className="space-y-6" data-id="element-297">
      <h2 className="text-2xl font-bold" data-id="element-298">Settings</h2>
      <div className="bg-white rounded-lg shadow p-4" data-id="element-299">
        <div className="flex items-center mb-4" data-id="element-300">
          <AlertTriangleIcon className="h-5 w-5 text-yellow-500 mr-2" data-id="element-301" />
          <h3 className="text-lg font-semibold" data-id="element-302">Alert Threshold Settings</h3>
        </div>
        <div className="max-w-md" data-id="element-303">
          <div className="mb-4" data-id="element-304">
            <label htmlFor="threshold" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-305">
              Energy Reading Threshold (kWh)
            </label>
            <div className="mt-1 flex rounded-md shadow-sm" data-id="element-306">
              <input type="number" id="threshold" min="0" step="1" className="flex-1 min-w-0 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" value={newThreshold} onChange={e => setNewThreshold(Number(e.target.value))} data-id="element-307" />
              <button onClick={handleSave} disabled={isSaving || newThreshold === threshold} className={`ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${isSaving || newThreshold === threshold ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'}`} data-id="element-308">
                {isSaving ? <>
                    <RefreshCwIcon className="h-4 w-4 mr-2 animate-spin" data-id="element-309" />
                    Saving...
                  </> : <>
                    <SaveIcon className="h-4 w-4 mr-2" data-id="element-310" />
                    Save
                  </>}
              </button>
            </div>
            <p className="mt-2 text-sm text-gray-500" data-id="element-311">
              Automatic alerts will be sent when energy readings drop below this
              threshold.
            </p>
          </div>
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded" data-id="element-312">
            <div className="flex" data-id="element-313">
              <div className="flex-shrink-0" data-id="element-314">
                <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-315" />
              </div>
              <div className="ml-3" data-id="element-316">
                <p className="text-sm text-yellow-700" data-id="element-317">
                  Setting the threshold too high may result in frequent alerts.
                  The current threshold is set to {threshold} kWh.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow p-4" data-id="element-318">
        <h3 className="text-lg font-semibold mb-4" data-id="element-319">Notification Settings</h3>
        <div className="max-w-md space-y-4" data-id="element-320">
          <div data-id="element-321">
            <label htmlFor="smsTemplate" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-322">
              SMS Alert Template
            </label>
            <textarea id="smsTemplate" rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" defaultValue={`ALERT: Energy reading has dropped to {value}kWh, which is below the threshold of {threshold}kWh.`} data-id="element-323" />
            <p className="mt-1 text-xs text-gray-500" data-id="element-324">
              Use {'{value}'} for the current reading and {'{threshold}'} for
              the threshold value.
            </p>
          </div>
          <div data-id="element-325">
            <label htmlFor="emailSubject" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-326">
              Email Alert Subject
            </label>
            <input type="text" id="emailSubject" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" defaultValue="Energy Meter Alert: Reading Below Threshold" data-id="element-327" />
          </div>
          <div data-id="element-328">
            <label htmlFor="emailTemplate" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-329">
              Email Alert Template
            </label>
            <textarea id="emailTemplate" rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" defaultValue={`Dear User,\n\nThis is an automated alert to inform you that the energy reading has dropped to {value}kWh, which is below the configured threshold of {threshold}kWh.\n\nPlease check the system for more details.`} data-id="element-330" />
            <p className="mt-1 text-xs text-gray-500" data-id="element-331">
              Use {'{value}'} for the current reading and {'{threshold}'} for
              the threshold value.
            </p>
          </div>
          <div className="pt-4" data-id="element-332">
            <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-333">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-334" />
              Save Templates
            </button>
          </div>
        </div>
      </div>
    </div>;
};
export default SettingsPage;